import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getEnvironment } from 'shared/utils/getEnvironment';
import {
  CopyToClipboard,
  HideString,
  ShowString,
} from 'shared/components/ui/ProjectIcons';
import { ApiPushDataType, CodeSampleSwitcher } from 'routes/codeSamples';
import {
  buttonClasses,
  configBoxClasses,
  buttonRowClasses,
} from 'styles/tailwindClassnames';

// Same titled box the action sections use, so every section reads alike.
const ConfigBox = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className={configBoxClasses.box} aria-label={title}>
    <div className={configBoxClasses.header}>
      <h3 className={configBoxClasses.title}>{title}</h3>
    </div>
    <div className={configBoxClasses.body}>{children}</div>
  </section>
);

const API_PUSH_DATA_TYPES = [
  {
    value: 'observations',
    label: 'Observations',
    hint: 'Positions from trackers, collars or vehicles.',
  },
  {
    value: 'events',
    label: 'Events',
    hint: 'Reports, alerts and incidents, with an optional location and details.',
  },
  {
    value: 'attachments',
    label: 'Events with attachments',
    hint: 'Create the event first, then send its files, such as camera-trap photos, using the object_id Gundi returns.',
  },
] as const satisfies readonly {
  value: ApiPushDataType;
  label: string;
  hint: string;
}[];

// How a data provider connects to Gundi: the endpoint to POST to and the API
// key, with sample code. Shared by the flow map's account panels and the
// Connection page so both offer the same Connect section.
const HowToConnect = ({
  kind,
  integrationType,
  apiKey,
  isAdmin,
}: {
  kind: string;
  integrationType: string;
  apiKey: string | undefined;
  isAdmin: boolean;
}) => {
  const { t } = useTranslation();
  const [keyVisible, setKeyVisible] = useState(false);
  const [dataType, setDataType] = useState<ApiPushDataType>('observations');
  const copyText = (text: string, label: string) =>
    navigator.clipboard
      .writeText(text)
      .then(() => toast.info(t('{{label}} copied to clipboard', { label })))
      .catch(() => toast.error(t('Failed to copy to clipboard')));
  const rawEnv = getEnvironment();
  const env = rawEnv === 'local' ? 'dev' : rawEnv;
  const sensorsApi =
    env === 'production'
      ? 'https://sensors.api.gundiservice.org/v2'
      : `https://sensors.api.${env}.gundiservice.org/v2`;
  const endpoint =
    kind === 'webhook'
      ? env === 'production'
        ? 'https://hooks.gundiservice.org/webhooks'
        : `https://hooks.${env}.gundiservice.org/webhooks`
      : dataType === 'observations'
        ? `${sensorsApi}/observations/`
        : `${sensorsApi}/events/`;
  const keyText = apiKey
    ? keyVisible && isAdmin
      ? apiKey
      : '••••••••••••••••'
    : t('Loading…');
  // Revealing and copying the key stays an admin action, as it was on the
  // provider tab this panel replaced.
  const keyActions = isAdmin && (
    <span className="ml-auto flex flex-shrink-0 items-center gap-2">
      <button
        type="button"
        onClick={() => setKeyVisible((v) => !v)}
        className="text-ui-text-secondary-text hover:text-ui-text-primary-text"
        aria-label={keyVisible ? t('Hide API key') : t('Show API key')}
      >
        {keyVisible ? <HideString /> : <ShowString />}
      </button>
      <button
        type="button"
        onClick={() => apiKey && copyText(apiKey, t('API key'))}
        className="text-ui-text-secondary-text hover:text-ui-text-primary-text"
        aria-label={t('Copy API key')}
      >
        <CopyToClipboard />
      </button>
    </span>
  );
  const endpointRow = (label: string, url: string, step?: number) => (
    <div
      key={url}
      className="flex items-center justify-between gap-4 border-b border-teal-t20 px-4 py-3 last:border-b-0"
    >
      <div className="flex min-w-0 flex-col">
        <span className="mb-0.5 text-body-sm text-ui-text-secondary-text">
          {step && `${step}. `}
          {label}
        </span>
        <span className="truncate font-mono text-body-sm text-ui-text-primary-text">
          {url}
        </span>
      </div>
      <button
        type="button"
        onClick={() => copyText(url, t('Endpoint'))}
        className="flex-shrink-0 text-ui-text-secondary-text hover:text-ui-text-primary-text"
        aria-label={t('Copy endpoint')}
      >
        <CopyToClipboard />
      </button>
    </div>
  );
  if (kind !== 'webhook') {
    const selected = API_PUSH_DATA_TYPES.find((d) => d.value === dataType);
    return (
      <div className="flex flex-col gap-4">
        <ConfigBox title={t('API key')}>
          <p className="mb-4 text-body-sm text-ui-text-primary-text">
            {t('Use this API key with every request you send to this Route.')}
          </p>
          <div className="flex items-center justify-between gap-4 rounded border border-teal-t20 bg-teal-t10 px-4 py-3">
            <div className="flex min-w-0 flex-col">
              <span className="mb-0.5 text-body-sm text-ui-text-secondary-text">
                {t('API key')}
              </span>
              <span className="truncate font-mono text-body-sm text-ui-text-primary-text">
                {keyText}
              </span>
            </div>
            {keyActions}
          </div>
        </ConfigBox>
        <ConfigBox title={t('Send data to this Route')}>
          <p className="mb-2 text-body-sm text-ui-text-primary-text">
            {t('What do you want to send?')}
          </p>
          <div
            role="group"
            aria-label={t('What do you want to send?')}
            className={`mb-2 ${buttonRowClasses.row}`}
          >
            {API_PUSH_DATA_TYPES.map((d) => (
              <button
                key={d.value}
                type="button"
                aria-pressed={d.value === dataType}
                onClick={() => setDataType(d.value)}
                // White rectangle buttons, like the webhook tab's, so the picker
                // doesn't look like the code language tabs below it.
                className={
                  d.value === dataType
                    ? buttonClasses.outlineXsSelected
                    : buttonClasses.outlineXs
                }
              >
                {t(d.label)}
              </button>
            ))}
          </div>
          {selected && (
            <p className="mb-4 text-body-sm text-ui-text-secondary-text">
              {t(selected.hint)}
            </p>
          )}
          <div className="mb-4 overflow-hidden rounded border border-teal-t20 bg-teal-t10">
            {dataType === 'attachments'
              ? [
                  endpointRow(t('Create the event'), endpoint, 1),
                  endpointRow(
                    t('Add files to the event'),
                    `${endpoint}{object_id}/attachments/`,
                    2,
                  ),
                ]
              : endpointRow(t('Endpoint'), endpoint)}
          </div>
          <p className="mb-4 text-body-sm text-ui-text-primary-text">
            {t(
              'Share this sample code and API key with any developers who need to push data to this Route. They can use it as a starting point to integrate their system directly.',
            )}
          </p>
          <CodeSampleSwitcher
            kind="api-push"
            dataType={dataType}
            endpoint={endpoint}
            apiKey={keyVisible && isAdmin ? apiKey || null : null}
            integrationType={integrationType}
          />
          <p className="mt-4 text-body-sm text-ui-text-primary-text">
            <a
              href="https://support.earthranger.com/developer_docs/gundi-api"
              target="_blank"
              rel="noreferrer"
              className="text-ui-cta-primary hover:underline"
            >
              {t("Learn more about Gundi's API.")}
            </a>
          </p>
        </ConfigBox>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <ConfigBox title={t('Webhook endpoint')}>
        <p className="mb-4 text-body-sm text-ui-text-primary-text">
          {t(
            'Configure your source system to POST to the Gundi webhook endpoint below. Include your API key and the integration-type header with every request.',
          )}
        </p>
        <div className="overflow-hidden rounded border border-teal-t20 bg-teal-t10">
          {endpointRow(t('Endpoint'), endpoint)}
          <div className="grid grid-cols-2 border-b border-teal-t20 bg-teal-t10 px-4 py-2">
            <span className="text-body-sm text-ui-text-secondary-text">
              {t('Header')}
            </span>
            <span className="text-body-sm text-ui-text-secondary-text">
              {t('Value')}
            </span>
          </div>
          <div className="grid grid-cols-2 border-b border-teal-t20">
            <div className="flex items-center justify-between gap-2 border-r border-teal-t20 px-4 py-3">
              <span className="font-mono text-body-sm text-ui-text-primary-text">
                apikey
              </span>
              <button
                type="button"
                onClick={() => copyText('apikey', t('Header name'))}
                className="flex-shrink-0 text-ui-text-secondary-text hover:text-ui-text-primary-text"
                aria-label={t('Copy header name')}
              >
                <CopyToClipboard />
              </button>
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <span className="flex-1 truncate font-mono text-body-sm text-ui-text-primary-text">
                {keyText}
              </span>
              {keyActions}
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="flex items-center justify-between gap-2 border-r border-teal-t20 px-4 py-3">
              <span className="font-mono text-body-sm text-ui-text-primary-text">
                x-gundi-integration-type
              </span>
              <button
                type="button"
                onClick={() =>
                  copyText('x-gundi-integration-type', t('Header name'))
                }
                className="flex-shrink-0 text-ui-text-secondary-text hover:text-ui-text-primary-text"
                aria-label={t('Copy header name')}
              >
                <CopyToClipboard />
              </button>
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <span className="truncate font-mono text-body-sm text-ui-text-primary-text">
                {integrationType}
              </span>
              <button
                type="button"
                onClick={() => copyText(integrationType, t('Header value'))}
                className="flex-shrink-0 text-ui-text-secondary-text hover:text-ui-text-primary-text"
                aria-label={t('Copy header value')}
              >
                <CopyToClipboard />
              </button>
            </div>
          </div>
        </div>
      </ConfigBox>
      <ConfigBox title={t('Sample code')}>
        <p className="mb-4 text-body-sm text-ui-text-primary-text">
          {t(
            'Share this sample code with any developers integrating with this Route. Every request must include the apikey and x-gundi-integration-type headers, a timestamp preferably in ISO 8601 format, latitude and longitude, and a device or source ID.',
          )}
        </p>
        <CodeSampleSwitcher
          kind="webhook"
          endpoint={endpoint}
          apiKey={keyVisible && isAdmin ? apiKey || null : null}
          integrationType={integrationType}
        />
        <p className="mt-4 text-body-sm text-ui-text-primary-text">
          <a
            href="https://support.earthranger.com/developer_docs/custom-webhook"
            target="_blank"
            rel="noreferrer"
            className="text-ui-cta-primary hover:underline"
          >
            {t("Learn more about Gundi's webhook API.")}
          </a>
        </p>
      </ConfigBox>
    </div>
  );
};

export default HowToConnect;
